import React, { useCallback, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Display,
  FlexDirection,
  Grid,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import Overlay from 'Overlay'
import { SocialShareSheetContainer } from 'SocialLinks/SocialShareSheet'
import { getMessage, SHARE_TITLE } from 'SocialLinks/socialReferralHelper'

import type { IRAFOffer } from '../ReferralPresentation'
import { getTimeDifference } from '../timeHelper'
import { RAFBoostCountdown } from './RAFBoostCountdown'
import { RAFBoostHeader } from './RAFBoostHeader'
import { RAFBoostInstructions } from './RAFBoostInstructions'
import { RAFBoostOfferBoxes } from './RAFBoostOfferBoxes'
import { RAFBoostShare } from './RAFBoostShare'

const AlignedContainer = styled(Box)`
  @media (min-width: 885px) {
    padding-bottom: 40px;
  }
  @media (max-width: 886px) {
    margin: 0 16px;
    padding-bottom: 0;
  }
`

const StickyMobileShareButton = styled(Box)`
  padding: 16px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 2;

  @media (min-width: 886px) {
    display: none;
  }
`

const RelativeBox = styled(Box)`
  position: relative;
  margin-bottom: -25px;
`

const FullWidthButton = styled(Button)`
  padding: 8px 16px;
  width: 100%;
`

interface Props {
  rafOffer: IRAFOffer
  expiry: Date
  userRAFLink: string
  userFirstName: string
  device: string
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: () => void
  trackingReferFriend: () => void
  referralCode: string
}

export const BoostedRafSharing = ({
  rafOffer,
  userFirstName,
  device,
  userRAFLink,
  expiry,
  trackUserFreeFoodLinkShare,
  trackingReferFriendSocialSharing,
  trackingReferFriend,
  referralCode,
}: Props) => {
  const { days, hours, minutes, seconds } = getTimeDifference(expiry)
  const [expiryDays, setExpiryDays] = useState<number>(days)
  const [expiryHours, setExpiryHours] = useState<number>(hours)
  const [expiryMinutes, setExpiryMinutes] = useState<number>(minutes)
  const [expirySeconds, setExpirySeconds] = useState<number>(seconds)
  const [isShareSheetOpen, setIsShareSheetOpen] = useState<boolean>(false)

  const onMobileShare = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({
          title: SHARE_TITLE,
          url: userRAFLink,
          text: getMessage(rafOffer),
        })
        .catch((error) => {
          // Dismissing the native sheet rejects with AbortError; anything else means the share
          // itself failed, so the in-page sheet takes over.
          if ((error as Error)?.name !== 'AbortError') {
            setIsShareSheetOpen(true)
          }
        })
    } else {
      setIsShareSheetOpen(true)
    }

    trackUserFreeFoodLinkShare()
  }, [trackUserFreeFoodLinkShare, userRAFLink, rafOffer])

  const closeShareSheet = useCallback(() => {
    setIsShareSheetOpen(false)
    trackingReferFriendSocialSharing()
  }, [trackingReferFriendSocialSharing])

  const setTimes = useCallback(() => {
    const timeDifference = getTimeDifference(expiry)
    setExpiryDays(timeDifference.days)
    setExpiryHours(timeDifference.hours)
    setExpiryMinutes(timeDifference.minutes)
    setExpirySeconds(timeDifference.seconds)
  }, [expiry])

  useEffect(() => {
    setInterval(() => {
      setTimes()
    }, 1000)
  }, [setTimes])

  return (
    <RelativeBox>
      <Grid alignItems={AlignItems.Center} justifyContent={JustifyContent.Center}>
        <Col
          size={[12, 8, 8, 8]}
          gridColumn={['1 / span 12', '3 / span 8', '3 / span 8', '3 / span 8']}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
        >
          <AlignedContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
          >
            <Box paddingBottom={6}>
              <RAFBoostHeader />
              <RAFBoostOfferBoxes rafOffer={rafOffer} />
            </Box>
            <RAFBoostCountdown
              days={expiryDays}
              minutes={expiryMinutes}
              hours={expiryHours}
              seconds={expirySeconds}
            />
            <RAFBoostShare
              userRAFLink={userRAFLink}
              userFirstName={userFirstName}
              trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
              device={device}
              trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
              trackingReferFriend={trackingReferFriend}
              isBoost
            />
          </AlignedContainer>
        </Col>
      </Grid>
      <Grid>
        <Col size={12}>
          <RAFBoostInstructions rafOffer={rafOffer} />
        </Col>
      </Grid>
      <StickyMobileShareButton>
        <FullWidthButton
          colorVariant={ButtonColorVariant.Primary}
          onClick={onMobileShare}
          width="100%"
        >
          Share your link
        </FullWidthButton>
      </StickyMobileShareButton>
      <Overlay open={isShareSheetOpen} from="bottom">
        <SocialShareSheetContainer onClose={() => closeShareSheet()} referralCode={referralCode} />
      </Overlay>
    </RelativeBox>
  )
}
